import { useState, useEffect, ReactNode } from 'react'
import { ChevronDown, ChevronUp, Camera, Fingerprint, CreditCard, Hash } from 'lucide-react'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { toast } from 'sonner'
import type { User } from '@/api/users'
import { updateUser, getUserFingerprints } from '@/api/users'
import type { UserDeviceEnrollment } from '@veridia/shared'
import { getEnrollments } from '@/api/enrollments'
import { FaceUploadModal } from '@/components/shared/FaceUploadModal'
import { FingerprintList } from './FingerprintList'

type Card = 'foto' | 'huellas' | 'tarjeta' | 'pin'

interface Props {
  user: User
  proxyUrl: string
  proxyToken: string
  onUserUpdated: (u: User) => void
}

export function BiometricosTab({ user, proxyUrl, proxyToken, onUserUpdated }: Props) {
  const [open, setOpen] = useState<Card | null>(null)
  const [faceModalOpen, setFaceModalOpen] = useState(false)
  const [enrolledDevices, setEnrolledDevices] = useState<UserDeviceEnrollment[]>([])
  const [fingerprintCount, setFingerprintCount] = useState(0)

  function refreshFingerprintCount() {
    getUserFingerprints(user.id)
      .then(r => setFingerprintCount(r.fingerprints.length))
      .catch(() => {})
  }

  useEffect(() => {
    refreshFingerprintCount()
  }, [user.id])

  const [cardNo, setCardNo] = useState(user.card_no ?? '')
  const [cardDevice, setCardDevice] = useState('')
  const [savingCard, setSavingCard] = useState(false)

  const [pin, setPin] = useState('')
  const [pinDevice, setPinDevice] = useState('')
  const [savingPin, setSavingPin] = useState(false)

  useEffect(() => {
    getEnrollments(user.id)
      .then(enrs => {
        const active = enrs.filter(e => e.status !== 'blocked')
        setEnrolledDevices(active)
        if (active.length > 0) {
          setCardDevice(active[0].device_id)
          setPinDevice(active[0].device_id)
        }
      })
      .catch(() => {})
  }, [user.id])

  function toggle(card: Card) {
    setOpen(prev => prev === card ? null : card)
  }

  async function saveCard() {
    setSavingCard(true)
    try {
      await updateUser(user.id, { cardNo })
      if (cardDevice) {
        const r = await fetch(`${proxyUrl}/users/${cardDevice}/${user.employee_no}/card`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${proxyToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ cardNo }),
        })
        if (!r.ok) { const b = await r.json() as { error?: string }; throw new Error(b.error ?? 'Error') }
      }
      toast.success('Tarjeta guardada')
      onUserUpdated({ ...user, card_no: cardNo })
      setOpen(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al guardar tarjeta')
    } finally {
      setSavingCard(false)
    }
  }

  async function savePin() {
    if (pin.length < 4) return
    setSavingPin(true)
    try {
      await updateUser(user.id, { pin })
      if (pinDevice) {
        const r = await fetch(`${proxyUrl}/users/${pinDevice}/${user.employee_no}/pin`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${proxyToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ pin }),
        })
        if (!r.ok) { const b = await r.json() as { error?: string }; throw new Error(b.error ?? 'Error') }
      }
      toast.success('PIN configurado')
      setPin('')
      setOpen(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al guardar PIN')
    } finally {
      setSavingPin(false)
    }
  }

  const cards: { key: Card; icon: ReactNode; label: string; status: string }[] = [
    {
      key: 'foto',
      icon: <Camera className="w-5 h-5" />,
      label: 'Foto',
      status: user.face_uploaded ? '✓ Registrada' : 'Sin foto',
    },
    {
      key: 'huellas',
      icon: <Fingerprint className="w-5 h-5" />,
      label: 'Huellas',
      status: fingerprintCount > 0
        ? `✓ Registrada${fingerprintCount > 1 ? 's' : ''} (${fingerprintCount})`
        : 'Sin huellas',
    },
    {
      key: 'tarjeta',
      icon: <CreditCard className="w-5 h-5" />,
      label: 'Tarjeta',
      status: user.card_no ? `#${user.card_no}` : 'Sin tarjeta',
    },
    {
      key: 'pin',
      icon: <Hash className="w-5 h-5" />,
      label: 'PIN',
      status: 'Configurar',
    },
  ]

  return (
    <div className="space-y-2">
      {cards.map(card => (
        <div key={card.key} className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggle(card.key)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-background/50 transition-colors"
          >
            <span className="text-text-muted">{card.icon}</span>
            <div className="flex-1">
              <p className="text-text-primary text-sm font-semibold">{card.label}</p>
              <p className="text-text-muted text-xs">{card.status}</p>
            </div>
            {open === card.key
              ? <ChevronUp className="w-4 h-4 text-text-muted" />
              : <ChevronDown className="w-4 h-4 text-text-muted" />}
          </button>

          {open === card.key && (
            <div className="px-4 pb-4 pt-1 border-t border-border">
              {card.key === 'foto' && (
                <div className="flex justify-center py-3">
                  <button
                    onClick={() => { setOpen(null); setFaceModalOpen(true) }}
                    className="flex items-center gap-2 bg-accent text-background text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-accent-hover transition-colors"
                  >
                    <Camera className="w-4 h-4" />
                    {user.face_uploaded ? 'Cambiar foto' : 'Agregar foto'}
                  </button>
                </div>
              )}

              {card.key === 'huellas' && (
                <FingerprintList
                  userId={user.id}
                  employeeNo={user.employee_no}
                  proxyUrl={proxyUrl}
                  proxyToken={proxyToken}
                  onChange={refreshFingerprintCount}
                />
              )}

              {card.key === 'tarjeta' && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs text-text-muted">Número de tarjeta</label>
                    <input
                      value={cardNo}
                      onChange={e => setCardNo(e.target.value)}
                      placeholder="Ej. 0012345678"
                      className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                    />
                  </div>
                  {enrolledDevices.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-xs text-text-muted">Sincronizar con dispositivo</label>
                      <select
                        value={cardDevice}
                        onChange={e => setCardDevice(e.target.value)}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                      >
                        {enrolledDevices.map(d => (
                          <option key={d.device_id} value={d.device_id}>{d.device_name ?? d.device_id}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    onClick={saveCard}
                    disabled={savingCard || !cardNo.trim()}
                    className="w-full bg-accent text-background text-sm font-semibold py-2 rounded-md hover:bg-accent-hover transition-colors disabled:opacity-40"
                  >
                    {savingCard ? 'Guardando...' : 'Guardar tarjeta'}
                  </button>
                </div>
              )}

              {card.key === 'pin' && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-xs text-text-muted">PIN (4–8 dígitos)</label>
                    <PasswordInput
                      inputMode="numeric"
                      value={pin}
                      onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      placeholder="••••"
                    />
                  </div>
                  {enrolledDevices.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-xs text-text-muted">Sincronizar con dispositivo</label>
                      <select
                        value={pinDevice}
                        onChange={e => setPinDevice(e.target.value)}
                        className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary"
                      >
                        {enrolledDevices.map(d => (
                          <option key={d.device_id} value={d.device_id}>{d.device_name ?? d.device_id}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    onClick={savePin}
                    disabled={savingPin || pin.length < 4}
                    className="w-full bg-accent text-background text-sm font-semibold py-2 rounded-md hover:bg-accent-hover transition-colors disabled:opacity-40"
                  >
                    {savingPin ? 'Guardando...' : 'Guardar PIN'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <FaceUploadModal
        open={faceModalOpen}
        onClose={() => setFaceModalOpen(false)}
        userId={user.id}
        userName={user.name}
        proxyUrl={proxyUrl}
        proxyToken={proxyToken}
        hasFace={user.face_uploaded}
        onUploaded={() => { onUserUpdated({ ...user, face_uploaded: true }); setFaceModalOpen(false) }}
      />
    </div>
  )
}
